import PropTypes from "prop-types";
import Button from "@shared/v2/Button";
import Checkbox from "@shared/v2/Checkbox";
import Tooltip from "@shared/v2/Tooltip";
import TextLink from "@shared/v2/TextLink";
import {
  CircleInfoSolidV6,
  SparklesSolidV6,
  ToggleOffSolidV6,
  ToggleOnSolidV6,
  VolumeHighSolidV6,
  VolumeSlashSolidV6,
} from "@shared/v2/Icomoon";
import Toggle from "@shared/v2/Toggle";
import DncBanner from "../PhoneCall/components/DncBanner";
import OptedOutBanner from "../PhoneCall/components/OptedOutBanner";

export const ConferenceConfirmationDescription = () => (
  <div className="tw-flex tw-items-center tw-gap-[4px]">
    <span className="tw-text-gray-50">Start a dialer session with a list of leads.</span>
    <TextLink
      href="https://www.brivityknowledge.com/docs/brivity-dialer?highlight=dialer"
      target="_blank"
      className="tw-font-semibold"
    >
      Learn More
    </TextLink>
  </div>
);

export const ConferenceConfirmationBody = ({
  consentChecked,
  featureFlags,
  isAdmin,
  loadingValidLeads,
  outboundNotificationMessage,
  outboundNotificationMessageCondition = null,
  outboundRecording = null,
  outboundRecordingEnabled,
  setConsentChecked,
  validLeadCounts,
  enableLeadSummary = true,
  setEnableLeadSummary = () => {},
}) => {
  const showLeadSummaryToggle =
    Boolean(featureFlags.dialerImprovementUpdates) && Boolean(featureFlags.dialerLeadSummary);

  return (
    <div className="phone-call">
      <div className="tw-w-full tw-text-12d tw-text-white tw-font-semibold">
        <div
          className={`tw-bg-gray-50 tw-py-10px tw-px-16px tw-rounded-t-[8px] ${featureFlags.callRecording ? "" : "tw-rounded-b-[8px]"}`}
        >
          {loadingValidLeads ? (
            <span>
              <i className="fa fa-spinner fa-pulse" /> Loading Selected Leads
            </span>
          ) : (
            <span>{`${validLeadCounts.validLeads} of ${validLeadCounts.totalLeads} contacts can be auto-dialed`}</span>
          )}
        </div>

        {!loadingValidLeads && (
          <DncBanner
            className={
              outboundRecordingEnabled || validLeadCounts.hasOptedOutPhoneLeads
                ? "!tw-rounded-[0px]"
                : "!tw-rounded-t-[0px]"
            }
          />
        )}
        {!loadingValidLeads && validLeadCounts.hasOptedOutPhoneLeads && (
          <OptedOutBanner
            className={outboundRecordingEnabled ? "!tw-rounded-[0px]" : "!tw-rounded-t-[0px]"}
          />
        )}

        {outboundRecordingEnabled && (
          <div className="tw-p-16px tw-rounded-b-[8px] tw-text-14d tw-font-normal tw-text-gray-75 tw-flex tw-justify-between tw-items-center tw-border-[1px] tw-border-solid tw-border-gray-10">
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <div className="tw-flex tw-gap-[12px] tw-items-center tw-font-semibold">
                Recording Settings
                <Tooltip
                  placement="top"
                  trigger={<CircleInfoSolidV6 className="tw-text-gray-50" />}
                  innerClassName="tw-max-w-[186px]"
                  multiline
                  content={
                    isAdmin
                      ? "Configure your recording options in Phone Settings"
                      : "Contact your admin if you want to change your call recording settings"
                  }
                />
              </div>
              <div className="tw-text-gray-75 tw-flex tw-gap-6px tw-items-center">
                {outboundRecording === "automatic" ? (
                  <>
                    <ToggleOnSolidV6 size="l" />
                    <span>Recording will start automatically at the beginning of the call</span>
                  </>
                ) : (
                  <>
                    <ToggleOffSolidV6 size="l" />
                    <span>Recording will not start until you press record for each call</span>
                  </>
                )}
              </div>
              <div className="tw-text-gray-75 tw-flex tw-gap-6px tw-items-center">
                {outboundNotificationMessage ? (
                  <>
                    <VolumeHighSolidV6 size="l" />
                    <span>
                      A notification message will play{" "}
                      {outboundNotificationMessageCondition === "check"
                        ? 'for contacts with area codes marked "2-party"'
                        : "when the recording starts"}
                    </span>
                  </>
                ) : (
                  <>
                    <VolumeSlashSolidV6 size="l" />
                    <span>No notification message will play when the recording starts</span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {showLeadSummaryToggle && (
        <div className="tw-mt-[16px] tw-flex tw-justify-between tw-items-start tw-p-[16px] tw-rounded-[8px] tw-border-solid tw-border-[1px] tw-border-gray-10">
          <div className="tw-flex tw-gap-[8px] tw-items-center">
            <div className="tw-flex-none tw-flex tw-justify-center tw-items-center tw-rounded-full tw-w-[48px] tw-h-[48px] tw-bg-semantic-purple-5">
              <SparklesSolidV6 size="l" className="tw-text-semantic-purple-100" />
            </div>
            <div className="tw-flex-1 tw-flex tw-flex-col">
              <h3 className="tw-text-18d tw-m-0">Gabbi Call Assistant</h3>
              <p className="tw-m-0 tw-text-12d tw-font-semibold tw-text-gray-50 tw-mr-[24px]">
                Get a quick summary of your lead’s conversation history, recent activity signals, and
                suggested call angles for lead generation.
              </p>
            </div>
          </div>
          <Toggle
            aria-label="Gabbi Call Assistant"
            onChange={() => setEnableLeadSummary?.((t) => !t)}
            checked={enableLeadSummary}
          />
        </div>
      )}
      {featureFlags.callRecordingAdjustments &&
        outboundRecordingEnabled &&
        outboundNotificationMessageCondition !== "always" && (
          <div className="tw-pt-[16px] tw-pl-[16px]">
            <Checkbox
              label="I agree to disclose recording and get consent at the start of every call."
              isRequired
              checked={consentChecked}
              onClick={() => setConsentChecked(!consentChecked)}
            />
          </div>
        )}
    </div>
  );
};

ConferenceConfirmationBody.propTypes = {
  consentChecked: PropTypes.bool.isRequired,
  featureFlags: PropTypes.shape({
    callRecording: PropTypes.bool,
    callRecordingAdjustments: PropTypes.bool,
    dialerLeadSummary: PropTypes.bool,
    dialerImprovementUpdates: PropTypes.bool,
  }).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  loadingValidLeads: PropTypes.bool.isRequired,
  outboundNotificationMessage: PropTypes.bool.isRequired,
  outboundNotificationMessageCondition: PropTypes.string,
  outboundRecording: PropTypes.string,
  outboundRecordingEnabled: PropTypes.bool.isRequired,
  setConsentChecked: PropTypes.func.isRequired,
  validLeadCounts: PropTypes.shape({
    totalLeads: PropTypes.number.isRequired,
    validLeads: PropTypes.number.isRequired,
    hasOptedOutPhoneLeads: PropTypes.bool.isRequired,
  }).isRequired,
  enableLeadSummary: PropTypes.bool,
  setEnableLeadSummary: PropTypes.func,
};

export const ConferenceConfirmationActions = ({
  closeLabel = "Close",
  closeConfirmation,
  createDialerSession,
  isLoading,
  startDisabled,
}) => (
  <>
    <Button size="medium" schema="tertiary" onClick={closeConfirmation}>
      {closeLabel}
    </Button>
    <Button size="medium" onClick={createDialerSession} isLoading={isLoading} disabled={startDisabled}>
      Start
    </Button>
  </>
);

ConferenceConfirmationActions.propTypes = {
  closeLabel: PropTypes.string,
  closeConfirmation: PropTypes.func.isRequired,
  createDialerSession: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  startDisabled: PropTypes.bool.isRequired,
};
